/**
 * Global state manager for AuraDoc
 * Manages pipeline modes, steppers, and selected languages, and project data.
 */
class StateManager {
    constructor() {
        this.state = {
            activeMode: 'proposal',
            proposalStep: 0,
            proposalMaxStep: 1,
            quickDocStep: 0,
            quickDocMaxStep: 1,
            selectedLangs: ['한국어'],
            projects: this._loadFromStorage('proposals_db', {}),
            currentProjectId: null,
            projectData: { name: "", rfp: "", context: "", gonogo: "", strategy: "", toc: [], customCols: [], slides: {} }
        };
        this.listeners = [];
    }

    _loadFromStorage(key, defaultVal) {
        try {
            return JSON.parse(localStorage.getItem(key)) || defaultVal;
        } catch(e) {
            return defaultVal;
        }
    }

    _saveToStorage(key, val) {
        try {
            localStorage.setItem(key, JSON.stringify(val));
        } catch(e) {}
    }

    getState() {
        return this.state;
    }

    setState(newState) {
        this.state = { ...this.state, ...newState };
        this.notifyListeners();
    }

    subscribe(listener) {
        this.listeners.push(listener);
        return () => {
            this.listeners = this.listeners.filter(l => l !== listener);
        };
    }

    notifyListeners() {
        this.listeners.forEach(listener => listener(this.state));
    }
    
    saveCurrentProject() {
        if (!this.state.currentProjectId) return;
        this.state.projects[this.state.currentProjectId] = { ...this.state.projectData };
        this._saveToStorage('proposals_db', this.state.projects);
        this.notifyListeners();
    }
    
    deleteProject(id) {
        delete this.state.projects[id];
        this._saveToStorage('proposals_db', this.state.projects);
        if (this.state.currentProjectId === id) {
            this.state.currentProjectId = null;
        }
        this.notifyListeners();
    }
    
    loadProject(id) {
        if (!this.state.projects[id]) return false;
        this.state.currentProjectId = id;
        this.state.projectData = JSON.parse(JSON.stringify(this.state.projects[id]));
        const projectMode = this.state.projectData.mode || 'proposal';
        this.state.activeMode = projectMode;
        
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('auradoc:switchPipelineMode', { detail: { mode: projectMode } }));
        }
        
        if (this.state.activeMode === 'quickdoc') {
            let targetStep = 1;
            if (this.state.projectData.quickDocSource && this.state.projectData.quickDocSource.trim() !== "") targetStep = 2;
            if (this.state.projectData.quickDocResults && Object.keys(this.state.projectData.quickDocResults).length > 0) targetStep = 3;
            this.state.quickDocMaxStep = targetStep;
            this.setState({ quickDocStep: targetStep });
        } else {
            // Determine max step based on data
            let targetStep = 1;
            if (this.state.projectData.gonogo && this.state.projectData.gonogo.trim() !== "") {
                targetStep = 2;
                if (this.state.projectData.strategy && this.state.projectData.strategy.trim() !== "") {
                    targetStep = 3;
                    if (this.state.projectData.toc && this.state.projectData.toc.length > 0) {
                        targetStep = 4;
                    }
                }
            }
            this.state.proposalMaxStep = targetStep;
            this.setState({ proposalStep: targetStep });
        }
        return true;
    }
    
    newProject() {
        this.state.currentProjectId = 'proj_' + Date.now();
        const modeName = this.state.activeMode === 'quickdoc' ? "신규 빠른 문서 프로젝트" : "신규 제안 프로젝트";
        this.state.projectData = { name: modeName, mode: this.state.activeMode, rfp: "", context: "", gonogo: "", strategy: "", toc: [], customCols: [], slides: {}, quickDocSource: "", quickDocResults: {}, quickDocConfig: {} };
        if (this.state.activeMode === 'quickdoc') {
            this.state.quickDocMaxStep = 1;
            this.setState({ quickDocStep: 1 });
        } else {
            this.state.proposalMaxStep = 1;
            this.setState({ proposalStep: 1 });
        }
        // Don't save immediately to avoid blank projects in dashboard
        // this.saveCurrentProject();
    }
}

export const appState = (typeof window !== 'undefined' && window.__AURA_APP_STATE__) || new StateManager();
if (typeof window !== 'undefined') window.__AURA_APP_STATE__ = appState;

// Maintain global window.appState reference for backward compatibility with older logic
if (typeof window !== 'undefined') {
    window.appState = appState.state;
}

appState.subscribe((newState) => {
    if (typeof window !== 'undefined') {
        window.appState = newState;
    }
});
